import { getComposePages, getEntityDetails } from 'graphql/queries/getComposePage';
import { useProductStore } from 'stores/product.store';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useQuery } from '@apollo/client';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';

import {
  API_PREVIEW,
  ContentType,
  ENTITY_ID_SEARCH_PARAMS,
  PREVIEW,
  SLUG_SEARCH_PARAMS,
} from '@/lib/constants';
import { sentryService } from '@/services/index';

const HeroBanner = React.lazy(() => import('@/components/ui/discovery/hero-banner'));
const VideoSection = React.lazy(() => import('@/components/ui/discovery/video-section'));
const FeatureSection = React.lazy(() => import('@/components/ui/discovery/feature-section'));
const DeviceOrTwoColumnSections = React.lazy(
  () => import('@/components/ui/discovery/device-or-two-column-sections')
);
const TestimonialSection = React.lazy(
  () => import('@/components/ui/discovery/testimonial-section')
);
const AddressCheckerSection = React.lazy(
  () => import('@/components/ui/discovery/address-checker-section')
);
const ContentTabs = React.lazy(() => import('@/components/ui/discovery/content-tabs-section'));
const FAQSection = React.lazy(() => import('@/components/ui/discovery/faq-section'));

const PlansSection = React.lazy(() => import('@/components/ui/discovery/plans-section'));
const PromoSection = React.lazy(() => import('@/components/ui/discovery/promo-section'));
const RailOrGridsSection = React.lazy(
  () => import('@/components/ui/discovery/rail-or-grids-Section')
);

type SectionRefs = {
  [key: string]: React.RefObject<HTMLDivElement>;
};

export const useComposePageData = () => {
  const { planTabIndex } = useProductStore(state => state);
  const [contentError, setContentError] = useState<any>(null);
  const [contentData, setContentData] = useState<any>(null);
  const [isContentLoading, setIsContentLoading] = useState<boolean>(true);
  const [isPreview, setIsPreview] = useState(false);
  const { pathname, search, hash } = useLocation();
  const planRef = useRef(null);
  const headerRef = useRef(null);
  const isInitialRefs = useRef(false);

  const sectionRefs = useRef<SectionRefs>({});

  const queryParams = new URLSearchParams(search);
  const slug = queryParams.get(SLUG_SEARCH_PARAMS);
  const entryId = queryParams.get(ENTITY_ID_SEARCH_PARAMS);
  const hasProcessedInitialQuery = useRef(false);

  useEffect(() => {
    setIsPreview(pathname.includes(PREVIEW));
    if (hasProcessedInitialQuery.current) return;

    setContentData(null); // Reset contentData if pathname changes
    sectionRefs.current = {}; // Clear previous refs

    hasProcessedInitialQuery.current = true;
  }, [pathname, hash]);

  const slugUrl =
    (isPreview && slug !== API_PREVIEW) || (!isPreview && pathname !== API_PREVIEW)
      ? slug || pathname
      : '';

  let normalizedSlug = slugUrl;

  const queryVariables = {
    usePreview: isPreview,
    slug: normalizedSlug,
    id: entryId,
  };

  let queryOptions: any = {
    skip: entryId
      ? (entryId === '' && slugUrl === API_PREVIEW) || isPreview === false
      : slugUrl === '' || slugUrl === API_PREVIEW, // Skip the query if pathname is '/api/preview'
    variables: queryVariables,
  };

  if (isPreview) {
    queryOptions.pollInterval = 2000;
    queryOptions.fetchPolicy = 'network-only';
  }

  const {
    data: composePageData,
    error: composePageDataError,
    loading: isComposePageDataLoading,
    refetch,
  } = useQuery(entryId ? getEntityDetails : getComposePages, queryOptions);

  const composePageContent: any = useContentfulLiveUpdates(composePageData);
  const hasSubMenu = Boolean(contentData?.NBJComposePages?.[0]?.subMenu?.fields?.menuSections);

  useEffect(() => {
    if (sentryService.isSentryServiceEnabled() && composePageDataError) {
      let endpoint = 'Compose Page for ' + pathname;
      sentryService.captureAPIErrors(composePageDataError, 'Graphql', endpoint);
    }
  }, [composePageDataError, pathname]);

  useEffect(() => {
    //ContentfulLivePreview
    if (isPreview && composePageContent) {
      setContentData(composePageContent);
      setContentError(composePageDataError);
      setIsContentLoading(isComposePageDataLoading);
    }
    if (!isPreview && !isComposePageDataLoading && composePageData && !composePageDataError) {
      setContentData(composePageData);
      setContentError(composePageDataError);
      setIsContentLoading(isComposePageDataLoading);
    }
  }, [
    pathname,
    composePageData,
    composePageDataError,
    isComposePageDataLoading,
    isPreview,
    composePageContent,
    refetch,
  ]);

  //ContentfulLivePreview
  const previewLink = isPreview ? [{ link: '/api/preview', text: 'Home' }] : [];

  // Memoize contentOrder so it doesn't recompute unless contentData changes
  const { order: contentOrder, seoHelmetContent } = useMemo(() => {
    if (!contentData) return { order: [], seoHelmetContent: [] };
    const order = [] as Array<React.ReactNode>;
    sectionRefs.current = {}; // Clear previous refs

    const childContent = entryId
      ? contentData?.NBJEntityDetails
      : contentData?.NBJComposePages?.[0]?.contentList || [];

    const seoHelmetContent = contentData?.NBJComposePages?.[0]?.seoMetadata?.fields;

    childContent?.forEach((module: any, index: number) => {
      const classNameReference = module?.fields?.classNameReference;
      if (classNameReference) {
        const dynamicRef = React.createRef<HTMLDivElement>();
        sectionRefs.current[classNameReference] = dynamicRef;
      }

      switch (module?.sys?.contentType?.sys?.id) {
        case ContentType.HERO_OR_VIDEO_TWO_COLUMN_SECTION:
          if (module?.fields?.contentModelType === ContentType.HERO_BANNER) {
            order.push(
              <HeroBanner
                sysId={module.sys.id}
                planRef={planRef}
                headerRef={headerRef}
                hasSubMenu={hasSubMenu}
                isPreview={isPreview}
                sectionRef={sectionRefs.current[classNameReference] || null}
              />
            );
          }
          if (module?.fields?.contentModelType === ContentType.VIDEO_SECTION) {
            order.push(
              <VideoSection
                sysId={module.sys.id}
                isPreview={isPreview}
                sectionRef={sectionRefs.current[classNameReference] || null}
              />
            );
          }
          break;
        case ContentType.FEATURE_OR_DEVICE_SECTION:
          if (module?.fields?.contentModelType === ContentType.FEATURE_SECTION) {
            order.push(
              <FeatureSection
                sysId={module.sys.id}
                isPreview={isPreview}
                sectionRef={sectionRefs.current[classNameReference] || null}
              />
            );
          }
          if (module?.fields?.contentModelType === ContentType.DEVICES_SECTION) {
            order.push(
              <DeviceOrTwoColumnSections
                componentType={ContentType.DEVICES_SECTION}
                sysId={module.sys.id}
                isPreview={isPreview}
                sectionRef={sectionRefs.current[classNameReference] || null}
              />
            );
          }
          break;
        case ContentType.TESTIMONIAL_SECTION:
          order.push(
            <TestimonialSection
              sysId={module.sys.id}
              isPreview={isPreview}
              sectionRef={sectionRefs.current[classNameReference] || null}
            />
          );
          break;
        case ContentType.CONTENT_RAIL_TABS_SECTION:
          order.push(
            <ContentTabs
              sysId={module.sys.id}
              isPreview={isPreview}
              sectionRef={sectionRefs.current[classNameReference] || null}
            />
          );
          break;
        case ContentType.PRODUCT_PROMO_SECTION:
          order.push(
            <PromoSection
              sysId={module.sys.id}
              planRef={planRef}
              headerRef={headerRef}
              isPreview={isPreview}
              sectionRef={sectionRefs.current[classNameReference] || null}
            />
          );
          break;
        case ContentType.TWO_COLUMN_SECTION_CAROUSEL:
          order.push(
            <DeviceOrTwoColumnSections
              componentType={ContentType.TWO_COLUMN_SECTION}
              sysId={module.sys.id}
              isPreview={isPreview}
              sectionRef={sectionRefs.current[classNameReference] || null}
            />
          );
          break;
        case ContentType.CONTENT_TAIL_AND_GRID_CHANNEL_COLLECTION:
          if (module?.fields?.contentModelType === ContentType.RAIL_AND_GRID_SECTION) {
            order.push(
              <RailOrGridsSection
                sysId={module.sys.id}
                componentType={ContentType.RAIL_AND_GRID_SECTION}
                isPreview={isPreview}
                sectionRef={sectionRefs.current[classNameReference] || null}
              />
            );
          }
          if (module?.fields?.contentModelType === ContentType.CHANNEL_RAIL_SECTION) {
            order.push(
              <RailOrGridsSection
                sysId={module.sys.id}
                componentType={ContentType.CHANNEL_RAIL_SECTION}
                isPreview={isPreview}
                sectionRef={sectionRefs.current[classNameReference] || null}
              />
            );
          }
          break;
        case ContentType.PRODUCT_COLLECTION_SECTION:
          order.push(
            <PlansSection
              sysId={module.sys.id}
              planRef={planRef}
              isPreview={isPreview}
              componentType={ContentType.PRODUCT_COLLECTION_SECTION}
              sectionRef={sectionRefs.current[classNameReference] || null}
            />
          );
          break;
        case ContentType.PRODUCT_COMPARE_PLANS:
          order.push(
            <PlansSection
              sysId={module.sys.id}
              planRef={null}
              isPreview={isPreview}
              componentType={ContentType.PRODUCT_COMPARE_PLANS}
              sectionRef={sectionRefs.current[classNameReference] || null}
            />
          );
          break;
        case ContentType.FAQ_SECTION:
          order.push(
            <FAQSection
              sysId={module.sys.id}
              isPreview={isPreview}
              sectionRef={sectionRefs.current[classNameReference] || null}
            />
          );
          break;
        case ContentType.ADDRESS_CHECKER_SECTION:
          order.push(
            <AddressCheckerSection
              sysId={module.sys.id}
              isPreview={isPreview}
              sectionRef={sectionRefs.current[classNameReference] || null}
            />
          );
          break;
        default:
          break;
      }
    });

    return {
      order,
      seoHelmetContent,
    };
  }, [contentData, entryId, isPreview]);

  return {
    previewLink,
    contentOrder,
    seoHelmetContent,
    isContentLoading,
    headerRef,
    planRef,
    isPreview,
    contentError,
    pathname,
    planTabIndex,
    sectionRefs,
    isInitialRefs,
    contentData,
  };
};
